/**
 * The settings pages' rules, as plain functions — C11, FR-A8, T-1.17, T-1.18.
 *
 * Every rule here is also the service's (`app/domain/firm.py`,
 * `app/data/states.py`), and the service is the authority. These exist so a
 * firm is told **before the request**: a mismatched GSTIN is refused here and
 * never sent (T-1.17's test), and the month dropdown round-trips to the 1–12
 * the column holds (T-1.18's).
 */

import { INDIAN_STATES } from '@fiveox/ui'

import type { DayHours, FirmProfile, Weekday, WorkingHours } from '../../../lib/api/firm'

// -- the states and their GST codes -----------------------------------------

/** GSTIN prefixes per state, current first — `app/data/states.py`'s table,
 *  keyed by `@fiveox/ui`'s own names. Three states keep an older code that
 *  registrations made before a reorganisation still begin with. */
export const GST_STATE_CODES: Record<(typeof INDIAN_STATES)[number], string[]> = {
  'Andhra Pradesh': ['37', '28'],
  'Arunachal Pradesh': ['12'],
  Assam: ['18'],
  Bihar: ['10'],
  Chhattisgarh: ['22'],
  Goa: ['30'],
  Gujarat: ['24'],
  Haryana: ['06'],
  'Himachal Pradesh': ['02'],
  Jharkhand: ['20'],
  Karnataka: ['29'],
  Kerala: ['32'],
  'Madhya Pradesh': ['23'],
  Maharashtra: ['27'],
  Manipur: ['14'],
  Meghalaya: ['17'],
  Mizoram: ['15'],
  Nagaland: ['13'],
  Odisha: ['21'],
  Punjab: ['03'],
  Rajasthan: ['08'],
  Sikkim: ['11'],
  'Tamil Nadu': ['33'],
  Telangana: ['36'],
  Tripura: ['16'],
  'Uttar Pradesh': ['09'],
  Uttarakhand: ['05'],
  'West Bengal': ['19'],
  'Andaman and Nicobar Islands': ['35'],
  Chandigarh: ['04'],
  'Dadra and Nagar Haveli and Daman and Diu': ['26', '25'],
  Delhi: ['07'],
  'Jammu and Kashmir': ['01'],
  Ladakh: ['38', '01'],
  Lakshadweep: ['31'],
  Puducherry: ['34'],
}

export const STATES: readonly string[] = INDIAN_STATES

export function isState(value: string | null | undefined): boolean {
  return Boolean(value && (STATES as string[]).includes(value))
}

// -- identifiers -------------------------------------------------------------

const SHAPES = {
  pan: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
  tan: /^[A-Z]{4}[0-9]{5}[A-Z]$/,
  gstin: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/,
} as const

const EXAMPLES = { pan: 'AAKFK1234C', tan: 'PNEK12345A', gstin: '27AAKFK1234C1Z5' } as const

/** Why an identifier is the wrong shape, or `null`. Blank is fine — each is
 *  optional (FR-A8 §4). */
export function shapeProblem(field: keyof typeof SHAPES, value: string | null | undefined) {
  const clean = (value ?? '').trim().toUpperCase()
  if (!clean) return null
  return SHAPES[field].test(clean)
    ? null
    : `That isn’t the shape of a ${field.toUpperCase()} — for example ${EXAMPLES[field]}.`
}

/** FR-A8 §4, the service's `gstin_problem` word for word in substance: the
 *  first two digits are the state's GST code, characters 3–12 the PAN — each
 *  checked only when both sides are given. */
export function gstinProblem({
  gstin,
  state,
  pan,
}: {
  gstin: string | null | undefined
  state: string | null | undefined
  pan: string | null | undefined
}): string | null {
  const g = (gstin ?? '').trim().toUpperCase()
  if (!g) return null
  const codes = state ? GST_STATE_CODES[state as keyof typeof GST_STATE_CODES] : undefined
  if (codes && !codes.includes(g.slice(0, 2))) {
    return `A GSTIN registered in ${state} begins ${codes[0]}. This one begins ${g.slice(0, 2)} — check the state, or the GSTIN.`
  }
  const p = (pan ?? '').trim().toUpperCase()
  if (p && g.slice(2, 12) !== p) {
    return 'Characters 3 to 12 of a GSTIN are the PAN, and these don’t match your PAN.'
  }
  return null
}

// -- months ------------------------------------------------------------------

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

/** 1–12 → the name. The column holds the number (FR-A8 §7). */
export function monthName(month: number): string {
  return MONTHS[Math.min(12, Math.max(1, Math.round(month))) - 1] ?? 'April'
}

/** The name → 1–12, or `null` for anything else. */
export function monthNumber(name: string): number | null {
  const index = (MONTHS as readonly string[]).indexOf(name)
  return index < 0 ? null : index + 1
}

// -- place of supply ---------------------------------------------------------

/** The `<select>` value that stands for `null` — *the firm's own state*. */
export const OWN_STATE = ''

export function placeOfSupplyOptions(firmState: string | null | undefined) {
  return [
    {
      value: OWN_STATE,
      label: firmState ? `The firm’s own state (${firmState})` : 'The firm’s own state',
    },
    ...STATES.map((name) => ({ value: name, label: name })),
  ]
}

export const toSupplyValue = (stored: string | null | undefined) => stored ?? OWN_STATE
export const fromSupplyValue = (value: string) => (value === OWN_STATE ? null : value)

// -- working hours -----------------------------------------------------------

export const WEEKDAYS: Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
}

export const DEFAULT_DAY: { open: string; close: string } = { open: '09:30', close: '18:30' }

/** A starting week for a firm that has given none: weekdays open, the
 *  weekend closed. Shown, never saved until the firm presses Save. */
export function startingHours(): WorkingHours {
  const days: Partial<Record<Weekday, DayHours>> = {}
  for (const day of WEEKDAYS) days[day] = day === 'sat' || day === 'sun' ? null : { ...DEFAULT_DAY }
  return { days, note: null }
}

/** *Copy Monday to weekdays* — Tuesday to Friday take Monday's hours, or
 *  close with it. The weekend is left as it is. */
export function copyMondayToWeekdays(hours: WorkingHours): WorkingHours {
  const monday = hours.days.mon ?? null
  const days = { ...hours.days }
  for (const day of ['tue', 'wed', 'thu', 'fri'] as Weekday[]) {
    days[day] = monday ? { ...monday } : null
  }
  return { ...hours, days }
}

/** The first problem with a week, or `null` — the service's rule. */
export function hoursProblem(hours: WorkingHours | null): string | null {
  if (!hours) return null
  for (const day of WEEKDAYS) {
    const slot = hours.days[day]
    if (!slot) continue
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(slot.open) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(slot.close)) {
      return `${WEEKDAY_LABELS[day]}: give both times.`
    }
    if (slot.close <= slot.open) return `${WEEKDAY_LABELS[day]}: the office closes after it opens.`
  }
  if ((hours.note ?? '').length > 200) return 'Keep the note to 200 characters.'
  return null
}

/** One line for the week — `Mon–Fri 09:30–18:30 · Sat 10:00–14:00`. Used by
 *  the hours section's collapsed summary. */
export function hoursSummary(hours: WorkingHours | null): string {
  if (!hours) return 'Not given'
  const groups: Array<{ from: Weekday; to: Weekday; slot: DayHours }> = []
  for (const day of WEEKDAYS) {
    const slot = hours.days[day] ?? null
    const last = groups[groups.length - 1]
    if (last && JSON.stringify(last.slot) === JSON.stringify(slot)) last.to = day
    else groups.push({ from: day, to: day, slot })
  }
  const open = groups.filter((g) => g.slot)
  if (open.length === 0) return 'Closed all week'
  const short = (d: Weekday) => WEEKDAY_LABELS[d].slice(0, 3)
  return open
    .map((g) => {
      const days = g.from === g.to ? short(g.from) : `${short(g.from)}–${short(g.to)}`
      return `${days} ${g.slot!.open}–${g.slot!.close}`
    })
    .join(' · ')
}

// -- what a section sends ----------------------------------------------------

/**
 * The fields of one section that differ from what was loaded — the only
 * fields that section's Save sends (T-1.17: *"each section saves only what it
 * sent"*). A blank string and `null` are one value, so tabbing through an
 * empty box is not a change.
 */
export function changedFields(
  original: Partial<FirmProfile>,
  draft: Partial<FirmProfile>,
  fields: Array<keyof FirmProfile>,
): Partial<FirmProfile> {
  const out: Record<string, unknown> = {}
  for (const field of fields) {
    const before = normalise(original[field])
    const after = normalise(draft[field])
    if (JSON.stringify(before) !== JSON.stringify(after)) out[field] = after
  }
  return out as Partial<FirmProfile>
}

function normalise(value: unknown): unknown {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed === '' ? null : trimmed
  }
  return value ?? null
}
